import {useEffect, useRef, useState} from 'react'
import type {ReactNode} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import styles from './AnimatedCollapse.module.css'

const DURATION_MS = 150

interface AnimatedCollapseProps {
  open: boolean
  /** When true, children stay mounted while collapsed. When false (default), children unmount after the collapse animation completes. */
  keepMounted?: boolean
  unclipWhenOpen?: boolean
  children: ReactNode
}

/**
 * Wraps content in a 150ms grid-rows expand/collapse animation.
 * While collapsed the region is marked `inert` to remove it from
 * the accessibility tree and tab order.
 */
export function AnimatedCollapse({open, keepMounted = false, unclipWhenOpen = false, children}: AnimatedCollapseProps) {
  const animationEnabled = isFeatureEnabled('agent_sessions_collapse_animation')

  if (!animationEnabled) {
    if (!open && !keepMounted) return null
    return (
      <div hidden={!open || undefined} inert={!open || undefined}>
        {children}
      </div>
    )
  }

  return (
    <AnimatedCollapseInner open={open} keepMounted={keepMounted} unclipWhenOpen={unclipWhenOpen}>
      {children}
    </AnimatedCollapseInner>
  )
}

function AnimatedCollapseInner({open, keepMounted = false, unclipWhenOpen = false, children}: AnimatedCollapseProps) {
  const [shouldRender, setShouldRender] = useState(keepMounted || open)
  const [isExpanded, setIsExpanded] = useState(open)
  const [isSettled, setIsSettled] = useState(open)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef<number | null>(null)
  const wasOpenRef = useRef(open)
  const wasKeptMountedRef = useRef(keepMounted)

  // Mount immediately when opening (set during render to avoid extra frame)
  if (open && !shouldRender) setShouldRender(true)
  // Collapse visually immediately when closing
  if (!open && isExpanded) setIsExpanded(false)
  if (!open && isSettled) setIsSettled(false)

  useEffect(() => {
    const clear = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
    clear()

    if (open) {
      // Double rAF ensures the 0fr state is painted before transitioning to 1fr
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setIsExpanded(true))
      })
    } else if (!keepMounted && (wasOpenRef.current || wasKeptMountedRef.current)) {
      timerRef.current = setTimeout(() => {
        setShouldRender(false)
        timerRef.current = null
      }, DURATION_MS)
    }

    wasOpenRef.current = open
    wasKeptMountedRef.current = keepMounted
    return clear
  }, [open, keepMounted])

  useEffect(() => {
    if (!unclipWhenOpen || !isExpanded || isSettled) return
    const timer = setTimeout(() => setIsSettled(true), DURATION_MS)
    return () => clearTimeout(timer)
  }, [isExpanded, isSettled, unclipWhenOpen])

  if (!shouldRender) return null

  return (
    <div
      className={styles.container}
      data-open={isExpanded || undefined}
      data-settled={(unclipWhenOpen && open && isExpanded && isSettled) || undefined}
      inert={!isExpanded || undefined}
    >
      <div className={styles.inner}>{children}</div>
    </div>
  )
}
